(function () {
  const TILE_WIDTH = 2000;
  const PATH_STEPS = 200;
  const COPY_SEPARATOR = '\u00A0\u00A0\u00A0•\u00A0\u00A0\u00A0';
  const SVG_NS = 'http://www.w3.org/2000/svg';
  // Loop wrap fine-tune in SVG path units. Negative = wrap earlier, positive = wrap later.
  const LOOP_WRAP_ADJUST = -6;

  function lerp(min, max, t) {
    return min + (max - min) * t;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function parseNumber(value, fallback) {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  const RIBBON_MOBILE_BREAKPOINT = 576;

  function getRibbonViewportKey() {
    if (window.matchMedia('(min-width: 1024px)').matches) return 'desk';
    if (window.matchMedia(`(min-width: ${RIBBON_MOBILE_BREAKPOINT}px)`).matches) {
      return 'tab';
    }
    return 'mob';
  }

  function isSafari() {
    const ua = navigator.userAgent.toLowerCase();
    return (
      ua.includes('safari') &&
      !ua.includes('chrome') &&
      !ua.includes('chromium') &&
      !ua.includes('crios') &&
      !ua.includes('fxios') &&
      !ua.includes('edgios')
    );
  }

  class AboutUsRibbon {
    constructor(root) {
      this.root = root;
      this.svg = root.querySelector('.about-us-ribbon__svg');
      this.path = root.querySelector('.about-us-ribbon__path');
      this.textEl = root.querySelector('.about-us-ribbon__text');

      this.textPath = null;
      this.rafId = null;
      this.lastTime = 0;

      // startOffset (path units)
      this.offset = 0;
      this.unitLength = 0; // one "unit" length in path units
      this.pathLength = TILE_WIDTH;
      this.displayWidth = TILE_WIDTH;

      this.resizeObserver = null;
      this.visibilityObserver = null;
      this.isInViewport = true;

      if (!this.svg || !this.path || !this.textEl) return;

      this.readConfig();
      this.render();
      this.bindResize();
      this.bindVisibility();
    }

    // Pause the loop while the ribbon is offscreen; offset is kept, so it resumes from the same spot.
    bindVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isInViewport = entry.isIntersecting;

            if (!this.isInViewport) {
              this.stop();
            } else if (!this.rafId && this.shouldAnimate && this.unitLength > 0) {
              this.start();
            }
          });
        },
        { rootMargin: '50% 0px' }
      );
      this.visibilityObserver.observe(this.root);
    }

    readConfig() {
      const rawText = (this.root.dataset.text || '').trim();
      const parts = rawText.split('|').map((p) => p.trim()).filter(Boolean);
      this.text = parts.join(COPY_SEPARATOR);
      this.ribbonViewportKey = getRibbonViewportKey();
      this.waveIntensity = clamp(this.resolveWaveIntensity(), 0, 200);
      this.waveCycles = clamp(this.resolveWaveCycles(), 1, 8);
      this.ribbonSize = clamp(parseNumber(this.root.dataset.ribbonSize, 7), 0, 14);
      this.fontSize = clamp(this.resolveTextSize(), 10, 120);
      this.speedSetting = clamp(parseNumber(this.root.dataset.speed, 50), 0, 100);
      this.direction = this.root.dataset.direction === 'right' ? 'right' : 'left';
      this.directionSign = this.direction === 'right' ? -1 : 1;

      this.speed = lerp(25, 160, this.speedSetting / 100);
      this.isFlat = this.waveIntensity <= 0;
      this.shouldAnimate =
        this.root.dataset.animate !== 'false' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const waveT = Math.min(this.waveIntensity, 100) / 100;
      this.amplitude = lerp(0, 120, waveT);

      const bandT = this.ribbonSize / 14;
      this.strokeWidth = lerp(
        this.fontSize * 1.1,
        this.fontSize * 1.58,
        bandT
      );

      if (this.isFlat) {
        this.viewBoxHeight = this.strokeWidth + this.fontSize * 0.2;
      } else {
        const waveSpan = this.amplitude * 2 + this.strokeWidth;
        const textBleed = this.fontSize * 0.45;
        this.viewBoxHeight = waveSpan + textBleed;
      }

      this.yCenter = this.viewBoxHeight / 2;
      this.displayWidth = this.getDisplayWidth();
      this.effectiveWaveCycles =
        this.waveCycles * (this.displayWidth / TILE_WIDTH);
      this.wavePhase = this.resolveWavePhase();
    }

    resolveTextSize() {
      const base = parseNumber(this.root.dataset.textSize, 18);
      const textSizeMob = parseNumber(this.root.dataset.textSizeMob, 0);
      const textSizeTab = parseNumber(this.root.dataset.textSizeTab, 0);

      if (this.ribbonViewportKey === 'desk') return base;

      if (this.ribbonViewportKey === 'tab') {
        return textSizeTab > 0 ? textSizeTab : base;
      }

      return textSizeMob > 0 ? textSizeMob : base;
    }

    resolveWaveIntensity() {
      const base = parseNumber(this.root.dataset.waveIntensity, 48);
      const waveIntensityMob = parseNumber(this.root.dataset.waveIntensityMob, 0);
      const waveIntensityTab = parseNumber(this.root.dataset.waveIntensityTab, 0);

      if (this.ribbonViewportKey === 'desk') return base;

      if (this.ribbonViewportKey === 'tab') {
        return waveIntensityTab > 0 ? waveIntensityTab : base;
      }

      return waveIntensityMob > 0 ? waveIntensityMob : base;
    }

    resolveWaveCycles() {
      const base = parseNumber(this.root.dataset.waveCycles, 2);
      const waveCyclesMob = parseNumber(this.root.dataset.waveCyclesMob, 0);

      if (this.ribbonViewportKey === 'mob' && waveCyclesMob > 0) {
        return waveCyclesMob;
      }

      return base;
    }

    resolveWavePhase() {
      const base = parseNumber(this.root.dataset.wavePhase, 0);
      const wavePhaseMob = parseNumber(this.root.dataset.wavePhaseMob, 0);

      if (this.ribbonViewportKey === 'mob' && wavePhaseMob !== 0) {
        return wavePhaseMob;
      }

      return base;
    }

    getHostWidth() {
      const host = this.root.closest('.about-us');
      return host?.offsetWidth || window.innerWidth;
    }

    getDisplayWidth() {
      const hostWidth = this.getHostWidth();
      return Math.max(TILE_WIDTH, hostWidth);
    }

    buildPathD() {
      const width = this.displayWidth;

      if (this.isFlat) {
        const y = this.yCenter.toFixed(2);
        return `M 0 ${y} L ${width} ${y}`;
      }

      let d = '';
      for (let i = 0; i <= PATH_STEPS; i += 1) {
        const x = (width / PATH_STEPS) * i;
        const phaseRad = (this.wavePhase * Math.PI) / 180;
        const y =
          this.yCenter +
          this.amplitude *
          Math.sin((x / width) * this.effectiveWaveCycles * Math.PI * 2 + phaseRad);
        d += `${i === 0 ? 'M' : ' L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      }
      return d;
    }

    ensureTextPath() {
      if (this.textPath) return;

      const href = `#${this.path.id}`;
      this.textPath = document.createElementNS(SVG_NS, 'textPath');
      this.textPath.setAttribute('href', href);
      this.textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'href', href);
      this.textPath.classList.add('about-us-ribbon__text-path');
      this.textPath.setAttribute('dominant-baseline', 'central');
      this.textPath.setAttribute('alignment-baseline', 'central');
      this.textEl.appendChild(this.textPath);
    }

    applyTextAlignment() {
      if (!this.textPath || !this.textPath.getBBox) return;

      this.textPath.setAttribute('dominant-baseline', 'central');
      this.textPath.setAttribute('alignment-baseline', 'central');

      if (this.isFlat) {
        this.textPath.removeAttribute('dy');
        return;
      }

      try {
        const bbox = this.textPath.getBBox();
        const dy = this.yCenter - (bbox.y + bbox.height / 2) - (this.fontSize * 0.08);
        if (Number.isFinite(dy)) {
          this.textPath.setAttribute('dy', String(dy.toFixed(2)));
        }
      } catch (_error) {
        /* getBBox may fail before layout */
      }
    }

    measureUnitLength(unit) {
      const tempText = document.createElementNS(SVG_NS, 'text');
      tempText.setAttribute('class', this.textEl.getAttribute('class'));
      tempText.style.fontSize = `${this.fontSize}px`;
      tempText.style.visibility = 'hidden';
      this.svg.appendChild(tempText);

      tempText.textContent = unit;
      const single = tempText.getComputedTextLength();

      tempText.textContent = unit + unit;
      const doubled = tempText.getComputedTextLength();

      this.svg.removeChild(tempText);

      if (doubled > single && single > 0) {
        const period = doubled - single;
        if (period > 0) return period;
      }

      return single > 0 ? single : unit.length * (this.fontSize * 0.55);
    }

    render() {
      this.svg.setAttribute(
        'viewBox',
        `0 0 ${this.displayWidth} ${this.viewBoxHeight.toFixed(2)}`
      );
      this.root.classList.toggle('is-flat', this.isFlat);

      this.root.style.setProperty(
        '--about-us-ribbon-view-height',
        String(Math.round(this.viewBoxHeight))
      );
      this.root.style.setProperty(
        '--about-us-ribbon-width',
        `${Math.round(this.displayWidth)}px`
      );
      this.root.style.setProperty('--about-us-ribbon-font-size', `${this.fontSize}px`);

      this.path.setAttribute('d', this.buildPathD());
      this.path.setAttribute('stroke-width', String(this.strokeWidth));
      this.pathLength = this.path.getTotalLength
        ? this.path.getTotalLength()
        : this.displayWidth;

      if (!this.text) {
        this.textEl.textContent = '';
        this.textPath = null;
        this.unitLength = 0;
        this.offset = 0;
        this.stop();
        return;
      }

      // Guarantee exactly one textPath per ribbon.
      this.textEl.textContent = '';
      this.textPath = null;
      this.offset = 0;
      this.unitLength = 0;

      this.textEl.setAttribute('font-size', `${this.fontSize}`);

      const unit = `${this.text}${COPY_SEPARATOR}`;
      this.ensureTextPath();
      this.unitLength = this.measureUnitLength(unit);

      // Repeat inside one textPath so modulo wrap never reveals empty segments.
      const neededText = this.pathLength * 3 + this.unitLength * 2;
      const repeats =
        this.unitLength > 0 ? Math.ceil(neededText / this.unitLength) + 2 : 1;
      this.textPath.textContent = unit.repeat(repeats);

      this.textPath.setAttribute('startOffset', `${(-this.offset).toFixed(2)}`);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.applyTextAlignment();
          if (this.shouldAnimate && this.unitLength > 0) this.start();
          else this.stop();
        });
      });
    }

    getWrapLength() {
      const adjust = isSafari() ? LOOP_WRAP_ADJUST : 0;
      return Math.max(1, this.unitLength + adjust);
    }

    tick(timestamp) {
      if (!this.lastTime) this.lastTime = timestamp;
      const delta = (timestamp - this.lastTime) / 1000;
      this.lastTime = timestamp;

      if (this.unitLength > 0) {
        const wrapLength = this.getWrapLength();
        this.offset += this.speed * delta * this.directionSign;
        this.offset = ((this.offset % wrapLength) + wrapLength) % wrapLength;
        if (this.textPath) this.textPath.setAttribute('startOffset', `${(-this.offset).toFixed(2)}`);
      }

      this.rafId = requestAnimationFrame((t) => this.tick(t));
    }

    start() {
      this.stop();
      if (!this.isInViewport) return;
      this.lastTime = 0;
      this.rafId = requestAnimationFrame((t) => this.tick(t));
    }

    stop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    bindResize() {
      const host = this.root.closest('.about-us') || this.root.parentElement;

      const onUpdate = () => {
        const nextWidth = this.getDisplayWidth();
        const nextRibbonViewport = getRibbonViewportKey();

        if (
          nextWidth === this.displayWidth &&
          nextRibbonViewport === this.ribbonViewportKey
        ) {
          return;
        }

        this.readConfig();
        this.render();
      };

      if (window.ResizeObserver && host) {
        this.resizeObserver = new ResizeObserver(onUpdate);
        this.resizeObserver.observe(host);
      } else {
        window.addEventListener('resize', onUpdate);
      }

      this.mqRibbonMob = window.matchMedia(
        `(min-width: ${RIBBON_MOBILE_BREAKPOINT}px)`
      );
      this.mqTab = window.matchMedia('(min-width: 768px)');
      this.mqDesk = window.matchMedia('(min-width: 1024px)');
      this.mqRibbonMob.addEventListener('change', onUpdate);
      this.mqTab.addEventListener('change', onUpdate);
      this.mqDesk.addEventListener('change', onUpdate);
    }
  }

  function init(root) {
    if (root.aboutUsRibbon) {
      root.aboutUsRibbon.stop();
      root.aboutUsRibbon.visibilityObserver?.disconnect();
    }
    root.aboutUsRibbon = new AboutUsRibbon(root);
  }

  function initAll(scope) {
    scope.querySelectorAll('[data-about-us-ribbon]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();

